import React, { useCallback, useEffect, useState } from 'react';
import { listActionCardFlags, updateActionCardFlagStatus } from '../../services/api';

const REASON_LABELS = {
  video: 'Video does not match',
  activity: 'Activity/resource does not match',
  learning_outcome: 'Learning outcome does not match',
  other: 'Other',
};

export default function ActionCardFlagsPanel() {
  const [status, setStatus] = useState('open');
  const [flags, setFlags] = useState<any>([]);
  const [loading, setLoading] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const [updatingId, setUpdatingId] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listActionCardFlags(status);
      setFlags(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Could not load flagged Action Cards.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const setFlagStatus = async (flag, nextStatus) => {
    setUpdatingId(flag.flag_id);
    setError('');
    try {
      await updateActionCardFlagStatus(flag.flag_id, nextStatus);
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not update the flag.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <section aria-labelledby="action-card-flags-heading" style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <h2 id="action-card-flags-heading" style={{ margin: 0, color: '#3256a2', fontSize: 18 }}>Flagged Action Cards</h2>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          Show
          <select value={status} onChange={(event) => setStatus(event.target.value)} style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 6 }}>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>
      {error && <div role="alert" style={{ color: '#991b1b', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>Loading flags…</div>
      ) : flags.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>No {status === 'all' ? '' : `${status} `}flags.</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {flags.map((flag) => (
            <li key={flag.flag_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 320px', fontSize: 13 }}>
                <div style={{ fontWeight: 800, color: '#1e3a5f' }}>
                  {flag.standard_code} · {REASON_LABELS[flag.reason] || flag.reason}
                </div>
                <div style={{ color: '#475569', marginTop: 2 }}>
                  {[flag.subject, flag.domain, flag.strand, flag.grade ? `Grade ${flag.grade}` : null].filter(Boolean).join(' · ')}
                </div>
                {flag.details && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{flag.details}</div>}
                <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                  {[flag.school_name, flag.reported_by ? `Reported by ${flag.reported_by}` : null, flag.created_at ? new Date(flag.created_at).toLocaleString() : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                type="button"
                disabled={updatingId === flag.flag_id}
                onClick={() => setFlagStatus(flag, flag.status === 'open' ? 'resolved' : 'open')}
                style={{ alignSelf: 'flex-start', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: flag.status === 'open' ? '#3256a2' : '#fff', color: flag.status === 'open' ? '#fff' : '#1f2937', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                {flag.status === 'open' ? 'Mark resolved' : 'Reopen'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
